import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Search } from "lucide-react";
import { api, inr } from "@/lib/api";

export const NavSearch = ({ onNavigate, className = "", testid = "nav-search" }) => {
  const [q, setQ] = useState("");
  const [sugg, setSugg] = useState([]);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    const term = q.trim();
    if (term.length < 2) {
      setSugg([]);
      return;
    }
    const t = setTimeout(() => {
      api.get("/products", { params: { q: term } })
        .then((r) => { setSugg(r.data.slice(0, 6)); setOpen(true); })
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (e) => {
    e.preventDefault();
    setOpen(false);
    onNavigate?.();
    navigate(q.trim() ? `/shop?q=${encodeURIComponent(q.trim())}` : "/shop");
  };

  return (
    <form onSubmit={go} className={`relative ${className}`} ref={boxRef} data-testid={testid}>
      <Search size={14} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 pointer-events-none" />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        onFocus={() => sugg.length && setOpen(true)}
        placeholder="Search products…"
        data-testid={`${testid}-input`}
        className="w-full bg-zinc-100 border border-zinc-200 rounded-full text-xs text-zinc-900 pl-9 pr-4 py-2.5 placeholder:text-zinc-400 focus:outline-none focus:border-gold/60 focus:bg-white transition-colors duration-300"
      />
      <AnimatePresence>
        {open && sugg.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 8 }}
            transition={{ duration: 0.2 }}
            className="absolute top-full right-0 mt-3 w-80 bg-white border border-zinc-200 rounded-2xl shadow-xl shadow-pink-100/40 overflow-hidden z-[60]"
            data-testid={`${testid}-suggestions`}
          >
            {sugg.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => { setOpen(false); setQ(""); onNavigate?.(); navigate(`/product/${p.id}`); }}
                data-testid={`${testid}-suggestion-${p.id}`}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-pink-50/60 transition-colors duration-200 text-left border-b border-zinc-50 last:border-0"
              >
                <div className="w-10 h-10 bg-[#faf7f7] border border-zinc-100 rounded-lg flex items-center justify-center shrink-0">
                  {p.image && <img src={p.image} alt="" className="max-w-full max-h-full object-contain" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-zinc-900 leading-snug line-clamp-1">{p.name}</p>
                </div>
                <span className="text-xs text-gold font-medium shrink-0">{inr(p.price)}</span>
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </form>
  );
};
